import type { HTMLAttributes, JSX } from 'react';

import { cn } from '../lib/cn';

export type CardVariant = 'flat' | 'elevated' | 'outlined';

const VARIANT_STYLES: Record<CardVariant, string> = {
  // A hairline and a soft shadow keep a white card visible on the slate-50
  // page background; a bare white fill all but disappears there.
  flat: 'bg-white border border-slate-200/80 shadow-sm',
  elevated: 'bg-white shadow-md',
  outlined: 'bg-white border border-slate-200',
};

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
}

/**
 * A grouping container for related data — stats, form sections, summaries.
 *
 * Forwards every remaining `div` attribute (`data-testid`, `aria-*`, event
 * handlers, ...) via the rest spread: a caller that needs to hook a test
 * id or an accessibility attribute onto the card itself should never have
 * to wrap it in an extra `div` just to carry that one attribute.
 */
export function Card({ variant = 'flat', className, ...props }: CardProps): JSX.Element {
  return <div className={cn('rounded-xl p-6', VARIANT_STYLES[variant], className)} {...props} />;
}
